import React, { useEffect } from 'react';
import {
    ContinuousFlow,
    hasIteration,
    entryKeyOf,
    visibleUpToFrontier,
} from '../continuous_flow.jsx';
import { resolveShortLoopName } from '../loops_navigation.jsx';
import { checkMissing } from '../item.jsx';
import { useFlowStore, useSessionStore, useFlowRegistry, useFlowStoreInstance } from '../../store/react';
import { selectLoopsDefinitions, selectLoopIterationName, selectCurrentItem } from '../../store/selectors';

// Where the engine currently sits, read from `path` rather than from the item
// definition: definitions are shared singletons whose loopName is reassigned during
// traversal, while the path is plain snapshot data.
function currentLoopContext(path, currentItemId) {
    if (!Array.isArray(path)) return {};
    for (let i = path.length - 1; i >= 0; i -= 1) {
        if (path[i] && path[i].itemId === currentItemId) {
            const loop = path[i].loop;
            return loop ? { loopName: loop.loopName, loopIndex: loop.loopIndex } : {};
        }
    }
    return {};
}

// The stored state of the item the engine sits on, wherever it lives: in the current
// iteration of its loop, or in the flat itemsStates for a non-loop item.
function currentItemState(snapshot, itemId) {
    const ctx = currentLoopContext(snapshot.path, itemId);
    const loopRecord = ctx.loopName ? snapshot.loops[ctx.loopName] : undefined;
    const iterationIndex = ctx.loopIndex || (loopRecord ? loopRecord.index : undefined);
    const state = loopRecord
        ? (loopRecord.states[iterationIndex - 1] || {})[itemId]
        : snapshot.itemsStates[itemId];
    return {
        state,
        loopName: ctx.loopName,
        loopIndex: ctx.loopName ? iterationIndex : undefined,
    };
}

export default function ContinuousFlowWrapper() {
    const registry = useFlowRegistry();
    const flowStore = useFlowStoreInstance();
    // Subscribe to everything the walk depends on so it is recomputed on any answer.
    useFlowStore((s) => s.itemsStates);
    useFlowStore((s) => s.loops);
    useFlowStore((s) => s.globals);
    useFlowStore((s) => s.path);
    const loops = useFlowStore((s) => s.loops);
    const currentItemId = useFlowStore((s) => s.currentItemId);
    const hasErrors = useFlowStore((s) => s.hasErrors);
    const loopsDefinitions = useFlowStore((s) => selectLoopsDefinitions(s, registry));
    const setAttribute = useFlowStore((s) => s.setAttribute);
    const advance = useFlowStore((s) => s.advance);
    const itemsStatesForSync = useFlowStore((s) => s.itemsStates);
    const mode = useSessionStore((s) => s.mode);
    const isObserver = mode === 'observer';

    // On a continuous page the reader moves down the form on their own, so the engine
    // has to follow them: otherwise the "current item" marker sits on the item they
    // finished several questions ago, and Continue claims required fields are missing
    // on an item they already completed.
    //
    // Move forward while the item the engine sits on is complete. Where it would land
    // comes from peekNextItemId() rather than from the walk, so this works the same
    // inside a loop — there, the next question of an iteration has no stored answer
    // yet, which is exactly where the walk stops.
    //
    // Two things are never done on the reader's behalf, and stay an explicit Continue:
    // finishing the flow, and an advance that cannot move (an incomplete loop sends
    // the engine straight back to the item it was on).
    useEffect(() => {
        if (isObserver) return;
        for (let guard = 0; guard < 30; guard += 1) {
            const state = flowStore.getState();
            if (state.hasErrors || state.currentItemId === 'theEnd') return;
            const item = selectCurrentItem(state, registry);
            if (!item) return;
            const { state: itemState } = currentItemState(state, state.currentItemId);
            if (checkMissing(item, itemState && itemState.values)) return;
            const next = flowStore.engine.peekNextItemId();
            if (!next || next === 'theEnd' || next === state.currentItemId) return;
            advance();
            // Defensive: peek and advance agreeing is not guaranteed, and a no-move
            // advance would otherwise spin here.
            if (flowStore.getState().currentItemId === state.currentItemId) return;
        }
    }, [itemsStatesForSync, loops, currentItemId, isObserver, advance, flowStore, registry]);

    // The walk covers the whole path reachable under the current answers, not just
    // what is answered — so it is a view of the entire form, and the engine's own
    // position (`currentItemId`) is what "Continue" acts from, exactly as in
    // one-item-at-a-time mode.
    // `theEnd` is a flow terminator, not a question: the walk includes it once the
    // last item is answered, where it would render as an empty card. Completion is
    // announced by the message under the list instead.
    const walk = flowStore.engine.traverse().filter((e) => e.item.id !== 'theEnd');

    // Inside a loop the walk stops at the first iteration item with no stored answer,
    // so the question the user actually has to answer next can be missing from it.
    // Append the engine's current position in that case, otherwise continuous mode
    // would show no editable question and could not progress through the loop.
    //
    // "Missing" is judged per iteration, not per item: from the second iteration on,
    // the same question is already in the walk for an *earlier* iteration, and an
    // item-id check would take that as the current one — leaving the question the
    // engine actually sits on unrendered and the marker on the wrong iteration.
    const snapshot = flowStore.getState();
    const currentContext = currentItemState(snapshot, currentItemId);
    const currentKey = entryKeyOf({
        item: { id: currentItemId },
        loopName: currentContext.loopName,
        loopIndex: currentContext.loopIndex,
    });
    const entries = [...walk];
    if (!walk.some((e) => entryKeyOf(e) === currentKey)) {
        const currentItem = selectCurrentItem(snapshot, registry);
        if (currentItem) {
            const { state, loopName, loopIndex } = currentContext;
            entries.push({
                item: currentItem,
                values: state && state.values,
                preliminaries: state && state.preliminaries,
                loopName,
                loopIndex,
            });
        }
    }

    // Reveal is strictly top-down: everything up to and including the first item with
    // an unanswered required field. Answering out of order therefore never makes
    // unrelated items appear further down the page.
    const visibleEntries = visibleUpToFrontier(entries, currentKey);

    const currentEntry = entries.filter((e) => entryKeyOf(e) === currentKey).pop();
    const currentMissing = currentEntry
        ? checkMissing(currentEntry.item, currentEntry.values)
        : false;

    // The target iteration is taken from the walk entry, never from the item
    // definition: definitions are shared singletons whose loopName is reassigned
    // during traversal. An entry whose loop has not started yet carries a loopName
    // but no iteration, and must be written as a plain (non-loop) item.
    const writeAttribute = (entry, fieldIndex, attribute, value) => {
        if (isObserver) return;
        const inLoop = hasIteration(entry);
        setAttribute(entry.item.id, attribute, fieldIndex, value, {
            loopName: inLoop ? entry.loopName : null,
            loopIndex: inLoop ? entry.loopIndex : undefined,
        });
    };

    return (
        <ContinuousFlow
            entries={visibleEntries}
            isObserver={isObserver}
            readOnly={isObserver}
            loopLabel={(loopName) => resolveShortLoopName(loopName, loopsDefinitions)}
            iterationLabel={(loopName, loopIndex) =>
                selectLoopIterationName(loopName, loopIndex, flowStore.getState(), registry)}
            totalIterationsOf={(loopName) => (loops[loopName] ? loops[loopName].states.length : 0)}
            currentIterationOf={(loopName) => (loops[loopName] ? loops[loopName].index : undefined)}
            onChange={writeAttribute}
            onItemPreliminaryChange={writeAttribute}
            onAdvance={() => advance()}
            canAdvance={!hasErrors && !currentMissing}
            isComplete={currentItemId === 'theEnd'}
            currentItemId={currentItemId}
            currentEntryKey={entryKeyOf(currentEntry)}
        />
    );
}
